import { useTranslation } from "react-i18next";
import { useLocation, useHistory } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler, UseFormRegister } from "react-hook-form";

import ErrorMessage from "@/components/error-message/error-message";
import CredentialsOtpInput from "@/components/otp-input/OtpInput";
import Footer from "@/components/footer/Footer";
import Header from "@/components/header/Header";

import { FormError } from "@/models/site-credentials/common/FormError";
import { OtpValidation } from "@/models/site-credentials/mobile/OtpValidation";
import { LoyaltyCardOtpOptionsResponse } from "@/models/site-credentials/loyaltycard/LoyaltyCardOtpOptionsResponse";

import { cardAssociationService } from "@/services/site-credentials/mobile/CardAssociationService";
import NeedHelpButton from "@/components/need-help-button/need-help-button";
import ButtonContainer from "@/components/button-container";
import { Button } from "@/components/ui/button";
import Form from "@/components/Form/Form";

type Form = OtpValidation & FormError;

export default function LoyaltyCardOtpValidation() {
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const location = useLocation<LoyaltyCardOtpOptionsResponse>();
  const contact =
    location.state != null ? location.state.properties.contact.contact : "";
  const jwtToken =
    location.state != null ? location.state.properties.jwtToken : "";
  const ANCHOR_NEED_HELP = "";

  const formState = useForm<Form>({
    mode: "onSubmit",
    reValidateMode: "onSubmit",
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    formState: { errors },
  } = formState;

  const handleOTPChange = (value: string) => {
    clearErrors();
    setValue("code", value.trim());
  };

  const onSubmit: SubmitHandler<OtpValidation> = async (data) => {
    setLoading(true);
    try {
      const response = await cardAssociationService.verifyOtp(
        data.code,
        location.state != null ? location.state.nextStep : ""
      );

      switch (response.kind) {
        case "success":
          history.push(response.value.nextStep.substring(4), {
            ...response.value,
            responseCode: "100003",
          });
          break;
        case "error":
          if (response.code === "100004") {
            setError("code", {
              message: t("otp_validation.invalid_code"),
            });
            break;
          } else if (response.code === "100015") {
            setError("submitError", {
              message: t("otp_validation.invalid_nonce"),
            });
            break;
          } else if (response.code === "100016") {
            setError("submitError", {
              message: t("otp_validation.invalid_nonce"),
            });
            break;
          }
          setError("submitError", { message: t("common.unexpected_error") });
          break;

        default:
          setError("submitError", { message: t("common.unexpected_error") });

          break;
      }

      setLoading(false);
    } catch (e) {
      setError("submitError", { message: t("common.unexpected_error") });
      setLoading(false);
    }
  };

  useEffect(() => {
    const otpValidator = (register: UseFormRegister<Form>) =>
      register("code", {
        required: {
          value: true,
          message: t("common.required_field"),
        },
        pattern: {
          value: /^[0-9]*$/,
          message: t("otp_validation.code_must_be_numeric"),
        },
      });
    otpValidator(register);
  }, [register, t]);

  const headerMessage =
    location.state.properties.contact.type === "MobilePhone"
      ? `${t(
        "card_association_otp_validation.header_message_phone"
      )} ${contact}`
      : `${t(
        "card_association_otp_validation.header_message_email"
      )} ${contact}`;

  return (
    <Form
      id={'loyalty_card_otp_validation_form_'}
      autoComplete="on"
      form={formState}
      onSubmit={(e) => {
        clearErrors();
        handleSubmit(onSubmit)(e);
      }}
    >
      <Header
        title={t("card_association_otp_validation.header_title")}
        subtitle={headerMessage}
        showLoading={loading}
        previousStepBackXTimes={1}
      />
      <CredentialsOtpInput
        isError={errors.code ? true : false}
        errorMsg={errors.code ? errors.code.message : ""}
        onChange={handleOTPChange}
        jwtToken={jwtToken}
        isLoyalty
        onResendClick={setLoading}
      />
      <ErrorMessage error={errors.submitError} isHtml={true} />
      <ButtonContainer>
        <Button type="submit">
          {t("common.next")}
        </Button>
        <NeedHelpButton anchor={ANCHOR_NEED_HELP} />
      </ButtonContainer>
    </Form>
  );
}
